/**
 * Auth events between tabs of the same origin (#110). A logout or a session
 * loss in one tab reaches the others at once instead of on their next poll.
 * BroadcastChannel where available, else a localStorage write the other tabs
 * see as a `storage` event. The sending tab never receives its own event.
 */

import type { SessionEndInfo } from './session-ended';
import { logger } from './logger';

export type AuthEvent =
  { type: 'logged-out' } | { type: 'session-ended'; info: SessionEndInfo };

const CHANNEL_NAME = 'imsight-auth';
const STORAGE_KEY = 'imsight-auth-event';

let channel: BroadcastChannel | null = null;

function getChannel(): BroadcastChannel | null {
  if (channel) return channel;
  if (typeof window === 'undefined' || !('BroadcastChannel' in window)) {
    return null;
  }
  channel = new window.BroadcastChannel(CHANNEL_NAME);
  return channel;
}

/** Only well-formed events get through; anything else is someone else's message. */
function parse(data: unknown): AuthEvent | null {
  if (!data || typeof data !== 'object') return null;
  const event = data as Record<string, unknown>;
  if (event.type === 'logged-out') return { type: 'logged-out' };
  if (event.type === 'session-ended') {
    const info = event.info as Record<string, unknown> | undefined;
    if (
      (info?.kind === 'expired' || info?.kind === 'access_ended') &&
      typeof info.reason === 'string'
    ) {
      return {
        type: 'session-ended',
        info: { kind: info.kind, reason: info.reason },
      };
    }
  }
  return null;
}

export function broadcastAuthEvent(event: AuthEvent): void {
  try {
    const bc = getChannel();
    if (bc) {
      bc.postMessage(event);
      return;
    }
    // Fallback: a fresh value each time (at) so repeated events still fire;
    // removed at once, so nothing stays in storage.
    window.localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ event, at: Date.now() })
    );
    window.localStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    logger.warn('Could not tell other tabs about the auth event:', error);
  }
}

/** Calls handler for every event from another tab. Returns an unsubscribe. */
export function listenForAuthEvents(
  handler: (event: AuthEvent) => void
): () => void {
  const deliver = (data: unknown) => {
    const event = parse(data);
    if (event) handler(event);
  };

  const bc = getChannel();
  if (bc) {
    const onMessage = (message: MessageEvent) => deliver(message.data);
    bc.addEventListener?.('message', onMessage);
    if (!bc.addEventListener) bc.onmessage = onMessage;
    return () => {
      if (bc.removeEventListener) bc.removeEventListener('message', onMessage);
      else bc.onmessage = null;
    };
  }

  const onStorage = (e: StorageEvent) => {
    if (e.key !== STORAGE_KEY || !e.newValue) return;
    try {
      deliver(JSON.parse(e.newValue).event);
    } catch {
      // not ours
    }
  };
  window.addEventListener('storage', onStorage);
  return () => window.removeEventListener('storage', onStorage);
}
